import { BoardLayout } from "@/components/board/BoardLayout";
import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { ProjectAvailabilityDock, type ProjectAvailabilityFocus } from "@/components/projects/ProjectAvailabilityDock";
import { ProjectBoard } from "@/components/projects/ProjectBoard";
import { ProjectsControls } from "@/components/projects/ProjectsControls";
import { TimelineDateSelector } from "@/components/projects/TimelineDateSelector";
import { OooProvider } from "@/contexts/OooContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { useLoggedInEmployee } from "@/contexts/LoggedInEmployeeContext";
import { useUserSettings } from "@/contexts/UserSettingsContext";
import { useOffices } from "@/hooks/useOffices";
import { useProjectBoardData } from "@/hooks/useProjectBoardData";
import { useQueryParams } from "@/hooks/useQueryParams";
import { useUserTimeZone } from "@/hooks/useUserTimeZone";
import { clampAvailabilityDockHeight, getDefaultAvailabilityDockHeight } from "@/utils/availabilityDockUtils";
import {
  calendarDateInTimeZone,
  currentWeekStartInTimeZone,
  toISODateString,
} from "@/utils/dateUtils";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function buildExpandedProjectsMap(projects: Array<{ id: string }>): Record<string, boolean> {
  return Object.fromEntries(projects.map((project) => [project.id, true] as const));
}

function toggleExpandedProject(
  prev: Record<string, boolean>,
  projectId: string,
): Record<string, boolean> {
  return { ...prev, [projectId]: !prev[projectId] };
}

// Persist each user's expanded/collapsed project state in localStorage, scoped by
// employee id so different users on the same device keep their own state.
const EXPANDED_PROJECTS_KEY_PREFIX = "expandedProjects";

type BoardLayoutStyle = CSSProperties & {
  "--availability-dock-height"?: string;
};

function expandedProjectsStorageKey(employeeId: string): string {
  return `${EXPANDED_PROJECTS_KEY_PREFIX}_${employeeId}`;
}

function readStoredExpandedProjects(employeeId: string): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(expandedProjectsStorageKey(employeeId));
    if (stored) return JSON.parse(stored);
  } catch {}
  return {};
}

function writeStoredExpandedProjects(
  employeeId: string,
  expandedProjects: Record<string, boolean>,
): void {
  try {
    localStorage.setItem(expandedProjectsStorageKey(employeeId), JSON.stringify(expandedProjects));
  } catch {}
}

interface ActiveProjectsTabProps {
  // Rendered as the first child of the board (the page-level tab switcher).
  header?: ReactNode;
}

/**
 * "Current" tab - the active projects board with its controls, availability
 * dock and timeline date selector.
 */
export function ActiveProjectsTab({ header }: ActiveProjectsTabProps) {
  const {
    projects,
    loading,
    error,
    refresh,
    addProjectOptimistically,
    confirmProjectOptimistically,
    removeProjectOptimistically,
    updateProjectOptimistically,
    refreshSilently,
  } = useProjectBoardData();
  const { employee } = useLoggedInEmployee();
  const employeeId = employee?.id ?? null;
  const { settings, loading: settingsLoading } = useUserSettings();
  const { selectedOffices } = useOffices();
  const { categories, loading: categoriesLoading } = useCategories();
  const userTimeZone = useUserTimeZone();
  const { get, setParam } = useQueryParams();
  const projectParam = get("project");
  const projectNameParam = get("projectName");
  const showAvailabilityDock = get("availability") === "1";
  const focusedProjectId =
    typeof projectParam === "string" && /^[0-9a-zA-Z-]+$/.test(projectParam)
      ? projectParam
      : null;

  const [searchPills, setSearchPills] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});
  const [showTimelineDateSelector, setShowTimelineDateSelector] = useState(false);
  const [availabilityFocus, setAvailabilityFocus] = useState<ProjectAvailabilityFocus | null>(null);
  const [availabilityDockHeight, setAvailabilityDockHeight] = useState(getDefaultAvailabilityDockHeight);
  const focusedAvailabilityAssignmentId = showAvailabilityDock
    ? availabilityFocus?.assignment.id ?? null
    : null;

  const boardLayoutStyle = useMemo<BoardLayoutStyle>(
    () => ({
      "--availability-dock-height": `${availabilityDockHeight}px`,
    }),
    [availabilityDockHeight]
  );

  // Collapsed-by-default categories (e.g. Sold - Active) the user has revealed via the
  // controls toggle. Absent = hidden, matching the section's collapsed_by_default behavior.
  const [revealedCategories, setRevealedCategories] = useState<Record<string, boolean>>({});

  // The categories whose sections render hidden by default and get a show/hide toggle.
  const collapsibleCategories = useMemo(
    () => categories.filter((category) => category.collapsed_by_default),
    [categories],
  );

  const toggleCategory = useCallback((categoryId: string) => {
    setRevealedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  }, []);

  // Track the employee whose expanded state we've initialized so we don't persist
  // the empty initial state over what's in storage, and don't re-initialize on
  // later renders (e.g. when projects refresh).
  const hydratedExpandedForEmployeeRef = useRef<string | null>(null);

  // Initialize expand/collapse from the user's settings once the user, their
  // settings, and the projects list are all loaded.
  //   - preserve OFF: always start from the configured default (all expanded or
  //     all collapsed); localStorage is ignored.
  //   - preserve ON:  restore the per-project state from localStorage, falling
  //     back to the configured default when nothing is stored yet.
  useEffect(() => {
    if (!employeeId || settingsLoading || loading) return;
    if (hydratedExpandedForEmployeeRef.current === employeeId) return;
    const defaultMap = settings.projectsExpandedByDefault
      ? buildExpandedProjectsMap(projects)
      : {};
    if (settings.preserveExpandCollapse) {
      const stored = readStoredExpandedProjects(employeeId);
      setExpandedProjects(Object.keys(stored).length > 0 ? stored : defaultMap);
    } else {
      setExpandedProjects(defaultMap);
    }
    hydratedExpandedForEmployeeRef.current = employeeId;
  }, [
    employeeId,
    settingsLoading,
    loading,
    settings.projectsExpandedByDefault,
    settings.preserveExpandCollapse,
    projects,
  ]);

  // Persist expand/collapse on every change — but only when the user has opted
  // to preserve it. With preserve off the board is intentionally ephemeral.
  useEffect(() => {
    if (!employeeId || hydratedExpandedForEmployeeRef.current !== employeeId) return;
    if (!settings.preserveExpandCollapse) return;
    writeStoredExpandedProjects(employeeId, expandedProjects);
  }, [employeeId, expandedProjects, settings.preserveExpandCollapse]);

  const activeSearchTerms = useMemo(
    () => [...searchPills, ...(searchInput.trim() ? [searchInput.trim()] : [])],
    [searchPills, searchInput],
  );

  // Track which focusedProjectId we've already processed, so we don't re-apply
  // the search pill when projects refresh.
  const processedFocusedProjectRef = useRef<string | null>(null);

  // When navigating with ?project=ID, pin the focused project as a search pill and
  // reveal its section if that category is hidden by default.
  useEffect(() => {
    // Reset tracking when focused project is cleared
    if (!focusedProjectId) {
      processedFocusedProjectRef.current = null;
      return;
    }

    // Wait for projects to load
    if (loading || projects.length === 0) return;

    // Only process each focusedProjectId once (don't re-trigger on projects refresh)
    if (processedFocusedProjectRef.current === focusedProjectId) return;

    const project = projects.find((p) => p.id === focusedProjectId);
    if (project) {
      setSearchPills([project.name]);
      setSearchInput("");
      setRevealedCategories((prev) => ({
        ...prev,
        [project.category_id]: true,
      }));
    }

    // Mark this focusedProjectId as processed
    processedFocusedProjectRef.current = focusedProjectId;
  }, [focusedProjectId, loading, projects]);

  // Deep links that only know a project's name (e.g. assignment rows in the audit
  // log) arrive as ?projectName=. Resolve it to the project's id and rewrite the
  // URL to the canonical ?project=<id> so the focus/expand/clear logic above takes
  // over. Done with replace so the name URL doesn't linger in history.
  useEffect(() => {
    if (!projectNameParam) return;
    if (loading || projects.length === 0) return;
    const target = projectNameParam.trim().toLowerCase();
    const match = projects.find((p) => p.name.trim().toLowerCase() === target);
    setParam("projectName", null, { replace: true });
    if (match) setParam("project", match.id, { replace: true });
  }, [projectNameParam, loading, projects, setParam]);

  const today = useMemo(() => {
    return toISODateString(currentWeekStartInTimeZone(userTimeZone));
  }, [userTimeZone]);
  const [timelineStartDate, setTimelineStartDate] = useState<string>(today);

  // A timezone change is an explicit request to see that zone's current week.
  // This does not interfere with manual date navigation because `today` only
  // changes when the effective timezone changes (or this component remounts).
  useEffect(() => {
    setTimelineStartDate(today);
  }, [today]);

  const currentDate = useMemo(
    () => toISODateString(calendarDateInTimeZone(new Date(), userTimeZone)),
    [userTimeZone],
  );

  const expandAllProjects = useCallback(() => {
    setExpandedProjects(buildExpandedProjectsMap(projects));
  }, [projects]);

  const collapseAllProjects = useCallback(() => {
    setExpandedProjects({});
  }, []);

  const expandProject = useCallback((projectId: string) => {
    setExpandedProjects((prev) => toggleExpandedProject(prev, projectId));
  }, []);

  const ensureProjectExpanded = useCallback((projectId: string) => {
    setExpandedProjects((prev) => ({ ...prev, [projectId]: true }));
  }, []);

  const handleSearchPillsChange = useCallback((pills: string[]) => {
    setSearchPills(pills);
    if (pills.length === 0 && searchInput.trim() === "" && get("project")) {
      setParam("project", null);
    }
  }, [searchInput, get, setParam]);

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchPills.length === 0 && value.trim() === "" && get("project")) {
      setParam("project", null);
    }
  }, [searchPills, get, setParam]);

  const resetExpandedProjectsScroll = useCallback(() => {
    requestAnimationFrame(() => {
      const scrollContainers = document.querySelectorAll('.project-assignments__table-scroll');
      scrollContainers.forEach((scrollContainer) => {
        scrollContainer.scrollLeft = 0;
      });
    });
  }, []);

  const handleTimelineDateChange = useCallback((date: string) => {
    setTimelineStartDate(date);
    resetExpandedProjectsScroll();
  }, [resetExpandedProjectsScroll]);

  const toggleAvailabilityDock = useCallback(() => {
    if (showAvailabilityDock) {
      setAvailabilityFocus(null);
      setParam("availability", null);
      return;
    }

    setAvailabilityFocus(null);
    setParam("availability", "1");
  }, [setParam, showAvailabilityDock]);

  const toggleTimelineDateSelector = useCallback(() => {
    setShowTimelineDateSelector((current) => !current);
  }, []);

  const closeAvailabilityDock = useCallback(() => {
    setAvailabilityFocus(null);
    setParam("availability", null);
  }, [setParam]);

  const clearAvailabilityFocus = useCallback(() => {
    setAvailabilityFocus(null);
  }, []);

  const openAvailabilityForAssignment = useCallback((focus: ProjectAvailabilityFocus) => {
    setAvailabilityFocus(focus);
    setParam("availability", "1");
  }, [setParam]);

  useEffect(() => {
    if (!showAvailabilityDock) {
      setAvailabilityFocus(null);
    }
  }, [showAvailabilityDock]);

  useEffect(() => {
    const handleResize = () => {
      setAvailabilityDockHeight((currentHeight) => clampAvailabilityDockHeight(currentHeight));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setAvailabilityFocus((currentFocus) => {
      if (!currentFocus) return currentFocus;

      const nextProject = projects.find((project) => project.id === currentFocus.project.id);
      const nextAssignment = nextProject?.project_assignments.find(
        (assignment) => assignment.id === currentFocus.assignment.id,
      );

      if (!nextProject || !nextAssignment) return null;
      return { project: nextProject, assignment: nextAssignment };
    });
  }, [projects]);

  return (
    <BoardLayout
      className={showAvailabilityDock ? "project-board--with-availability-dock" : undefined}
      style={boardLayoutStyle}
    >
      {header}
      <ProjectsControls
        searchPills={searchPills}
        onSearchPillsChange={handleSearchPillsChange}
        searchInput={searchInput}
        onSearchInputChange={handleSearchInputChange}
        onExpandAll={expandAllProjects}
        onCollapseAll={collapseAllProjects}
        showAvailabilityDock={showAvailabilityDock}
        onToggleAvailabilityDock={toggleAvailabilityDock}
        showTimelineDateSelector={showTimelineDateSelector}
        onToggleTimelineDateSelector={toggleTimelineDateSelector}
        collapsibleCategories={collapsibleCategories}
        revealedCategories={revealedCategories}
        onToggleCategory={toggleCategory}
      />
      {error && <ErrorBanner message={error} entityName="projects" />}
      <OooProvider>
        {loading || categoriesLoading ? (
          <LoadingState message="Loading projects..." />
        ) : (
          <>
            <ProjectBoard
              projects={projects}
              loading={loading}
              refresh={refresh}
              addProjectOptimistically={addProjectOptimistically}
              confirmProjectOptimistically={confirmProjectOptimistically}
              removeProjectOptimistically={removeProjectOptimistically}
              updateProjectOptimistically={updateProjectOptimistically}
              refreshSilently={refreshSilently}
              searchTerms={activeSearchTerms}
              selectedOffices={selectedOffices}
              expandedProjects={expandedProjects}
              expandProject={expandProject}
              ensureProjectExpanded={ensureProjectExpanded}
              timelineStartDate={timelineStartDate}
              today={currentDate}
              onOpenAvailabilityForAssignment={openAvailabilityForAssignment}
              focusedAvailabilityAssignmentId={focusedAvailabilityAssignmentId}
              revealedCategories={revealedCategories}
            />
            {showTimelineDateSelector ? (
              <TimelineDateSelector
                value={timelineStartDate}
                onChange={handleTimelineDateChange}
                timeZone={userTimeZone}
              />
            ) : null}
          </>
        )}
        {showAvailabilityDock ? (
          <ProjectAvailabilityDock
            timelineStartDate={timelineStartDate}
            focus={availabilityFocus}
            heightPx={availabilityDockHeight}
            onHeightChange={setAvailabilityDockHeight}
            updateProjectOptimistically={updateProjectOptimistically}
            refreshSilently={refreshSilently}
            onClearFocus={clearAvailabilityFocus}
            onClose={closeAvailabilityDock}
          />
        ) : null}
      </OooProvider>
    </BoardLayout>
  );
}
